import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowDown, ArrowUp, Layers, Share2, Sparkles, X } from 'lucide-react';
import { SelectDropdown } from '../SelectDropdown';
import { AudienceLevel, Cohort, LEVEL_LABELS } from '../../types/pathway';
import { moduleById } from '../../data/skillModules';
import { formatHours, totalHours } from '../../utils/pathwayMatching';
import { PathwayButton } from './PathwayButton';
interface PathwayTrayProps {
  name: string;
  /** Prospect or company this pathway is for — appears on the PDF cover. */
  company: string;
  /** True when the company shown was picked up from the conversation notes. */
  companyFromNotes: boolean;
  /** Module ids in the order the prospect should take them. */
  items: string[];
  audience: AudienceLevel | 'any';
  cohortId: string;
  cohorts: Cohort[];
  /** Set once the rep has chosen how edits to a saved pathway should land. */
  editContext: string | null;
  onRevert: () => void;
  onNameChange: (name: string) => void;
  onCompanyChange: (company: string) => void;
  onAudienceChange: (audience: AudienceLevel | 'any') => void;
  onCohortChange: (cohortId: string) => void;
  onRemove: (moduleId: string) => void;
  onMove: (moduleId: string, direction: -1 | 1) => void;
  onClear: () => void;
  onShare: () => void;
}
export function PathwayTray({
  name,
  company,
  companyFromNotes,
  items,
  audience,
  cohortId,
  cohorts,
  editContext,
  onRevert,
  onNameChange,
  onCompanyChange,
  onAudienceChange,
  onCohortChange,
  onRemove,
  onMove,
  onClear,
  onShare
}: PathwayTrayProps) {
  // Company and pathway name both appear on the document a prospect reads, so
  // neither can be missing by the time it is generated.
  const missing: string[] = [];
  if (items.length === 0) missing.push('at least one module');
  if (!company.trim()) missing.push('a company');
  if (!name.trim()) missing.push('a pathway name');
  const canSend = missing.length === 0;
  const blockedReason = canSend ? null : `Add ${missing.length === 1 ? missing[0] : `${missing.slice(0, -1).join(', ')} and ${missing[missing.length - 1]}`} to send this pathway.`;
  return <aside aria-label="Your pathway" className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm lg:sticky lg:top-6" data-id="element-1685" data-mp="misd31y9xc42">
      <AnimatePresence initial={false} data-id="element-1686" data-mp="riwv4wetyewe">
        {editContext && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} className="overflow-hidden bg-[#30718D]/5" data-id="element-1687" data-mp="wfna1gewf4g8">
            <div className="flex items-start gap-2 px-5 py-3" data-id="element-1688" data-mp="1a71u7qkjlbov">
              <p className="flex-1 text-xs font-medium text-[#30718D]" data-id="element-1689" data-mp="1f7g31atywpny">
                {editContext}
              </p>
              <button type="button" onClick={onRevert} className="flex-shrink-0 text-xs font-medium text-gray-500 transition-colors hover:text-gray-900" data-id="element-1690" data-mp="1k5hmhwvb2qzy">
                Revert
              </button>
            </div>
          </motion.div>}
      </AnimatePresence>

      <div className="border-b border-gray-100 p-5" data-id="element-1691" data-mp="2uqnok41tbbrp">
        <div className="flex items-center justify-between" data-id="element-1692" data-mp="2prkkomxkjblb">
          <h2 className="text-lg font-bold text-gray-900" data-id="element-1693" data-mp="2pt5hykb9p8mv">Your pathway</h2>
          {items.length > 0 && <button type="button" onClick={onClear} className="text-xs font-medium text-gray-500 transition-colors hover:text-[#DB3725]" data-id="element-1694" data-mp="3ngnmfixwbol2">
              Clear
            </button>}
        </div>
        <p className="mt-1 text-sm text-gray-600" data-id="element-1695" data-mp="38p5ca1rpwo9m">
          {items.length === 0 ? 'Nothing added yet' : `${items.length} ${items.length === 1 ? 'module' : 'modules'} · ${formatHours(totalHours(items))} total`}
        </p>
      </div>

      <div className="space-y-3 border-b border-gray-100 p-5" data-id="element-1696" data-mp="3lvja2be6ycg3">
        <label className="block" data-id="element-1697" data-mp="3qve3ompcpg4u">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-gray-500" data-id="element-1698" data-mp="3qwp9es2cwxyl">
            Company
            <span aria-hidden="true" className="ml-0.5 text-[#DB3725]" data-id="element-1699" data-mp="3c07vbt2s958c">
              *
            </span>
          </span>
          <input value={company} onChange={e => onCompanyChange(e.target.value)} placeholder="e.g. Meridian Health" required aria-required="true" className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#30718D]" data-id="element-1700" data-mp="3gzb15fql7fhd" />
          {companyFromNotes && <span className="mt-1 flex items-center gap-1 text-xs text-[#30718D]" data-id="element-1701" data-mp="rg0md54qcpmu">
              <Sparkles size={11} aria-hidden="true" data-id="element-1702" data-mp="w6z5sdl0m3ub" />
              From your notes — edit if that’s not right
            </span>}
        </label>

        <label className="block" data-id="element-1703" data-mp="2gw358dx4363a">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-gray-500" data-id="element-1704" data-mp="2bykvwmf6mour">
            Pathway name
            <span aria-hidden="true" className="ml-0.5 text-[#DB3725]" data-id="element-1705" data-mp="2qnqtk5ydg70j">
              *
            </span>
          </span>
          <input value={name} onChange={e => onNameChange(e.target.value)} placeholder="e.g. New Manager Track" required aria-required="true" className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#30718D]" data-id="element-1706" data-mp="2lppahl0y47p2" />
        </label>

        <div data-id="element-1707" data-mp="22wbpa7n1bfmn">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-gray-500" data-id="element-1708" data-mp="22zzit6shc21d">
            Built for
          </span>
          <SelectDropdown ariaLabel="Audience this pathway is built for" fullWidth scrollMenu value={audience} onChange={value => onAudienceChange(value as AudienceLevel | 'any')} options={[{
          value: 'any',
          label: 'Mixed audience'
        }, ...(Object.keys(LEVEL_LABELS) as AudienceLevel[]).map(level => ({
          value: level,
          label: LEVEL_LABELS[level]
        }))]} data-id="element-1709" data-mp="3edl4uj31lnio" />
        </div>

        <div data-id="element-1710" data-mp="1xa7yc5akkrwk">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-gray-500" data-id="element-1711" data-mp="1xbst9w0sqcuy">
            Cohort (optional)
          </span>
          <SelectDropdown ariaLabel="Cohort for this pathway" fullWidth scrollMenu value={cohortId} onChange={onCohortChange} options={[{
          value: '',
          label: 'No cohort selected'
        }, ...cohorts.map(cohort => ({
          value: cohort.id,
          label: `${cohort.organization} — ${cohort.name} (${cohort.size})`
        }))]} data-id="element-1712" data-mp="21ai3jfqjfead" />
        </div>
      </div>

      <div className="max-h-[420px] overflow-y-auto p-5" data-id="element-1713" data-mp="112bnyhr3h98x">
        {items.length === 0 ? <div className="py-8 text-center" data-id="element-1714" data-mp="x4nod1egisap">
            <Layers size={24} className="mx-auto text-gray-400" data-id="element-1715" data-mp="s49fjh517ebm" />
            <p className="mx-auto mt-3 max-w-[15rem] text-sm text-gray-600" data-id="element-1716" data-mp="n4dadekllguh">
              Add modules from the results, then put them in the order the
              prospect should take them.
            </p>
          </div> : <ul className="space-y-2" data-id="element-1717" data-mp="ddsc5s8u6ec3">
            <AnimatePresence initial={false} data-id="element-1718" data-mp="dettufkcxz2s">
              {items.map((moduleId, index) => {
            const module = moduleById(moduleId);
            if (!module) return null;
            return <motion.li key={moduleId} layout initial={{
              opacity: 0,
              y: -6
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              x: 8
            }} transition={{
              duration: 0.18,
              ease: [0.23, 1, 0.32, 1]
            }} className="flex items-start gap-2 rounded-lg border border-gray-200 bg-white p-3" data-id="element-1719" data-mp="4fq62ya8wvhy" data-mpi={index}>
                    <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded bg-gray-100 text-xs font-bold text-gray-600" data-id="element-1720" data-mp="14kzezgj8mfzx">
                      {index + 1}
                    </span>
                    <div className="min-w-0 flex-1" data-id="element-1721" data-mp="vkuhby3uivyy">
                      <p className="text-sm font-semibold text-gray-900" data-id="element-1722" data-mp="vm5irr9tjrmw">
                        {module.name}
                      </p>
                      <p className="mt-0.5 text-xs text-gray-500" data-id="element-1723" data-mp="lmeqlw8qdopz">
                        {formatHours(module.durationHours)} · {module.category}
                      </p>
                    </div>
                    <div className="flex flex-shrink-0 items-center" data-id="element-1724" data-mp="6z680ad3eh3z">
                      <button type="button" onClick={() => onMove(moduleId, -1)} disabled={index === 0} aria-label={`Move ${module.name} earlier`} className="rounded p-1 text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-30" data-id="element-1725" data-mp="71iosk746lfg">
                        <ArrowUp size={14} data-id="element-1726" data-mp="kzh35plfqhuy" />
                      </button>
                      <button type="button" onClick={() => onMove(moduleId, 1)} disabled={index === items.length - 1} aria-label={`Move ${module.name} later`} className="rounded p-1 text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-30" data-id="element-1727" data-mp="kxcwx102d6ri">
                        <ArrowDown size={14} data-id="element-1728" data-mp="2ap41kpwaursa" />
                      </button>
                      <button type="button" onClick={() => onRemove(moduleId)} aria-label={`Remove ${module.name}`} className="rounded p-1 text-gray-400 transition-colors hover:bg-gray-50 hover:text-[#DB3725]" data-id="element-1729" data-mp="25xlx3rv2i92k">
                        <X size={14} data-id="element-1730" data-mp="2tllq6yfuqbkb" />
                      </button>
                    </div>
                  </motion.li>;
          })}
            </AnimatePresence>
          </ul>}
      </div>

      <div className="border-t border-gray-100 p-5" data-id="element-1731" data-mp="2jmxk8y155mwz">
        <PathwayButton fullWidth onClick={onShare} disabled={!canSend} data-id="element-1732" data-mp="2fnornwhu7v30">
          <Share2 size={18} data-id="element-1733" data-mp="189lo79bmi8ld" />
          Review and Send
        </PathwayButton>
        <p className="mt-2 text-center text-xs text-gray-500" data-id="element-1734" data-mp="187ha5yk0ft7d">
          {blockedReason ?? 'Creates a branded PDF and a shareable link.'}
        </p>
      </div>
    </aside>;
}